import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import PriorityCheckbox from '@/components/shared/PriorityCheckbox';
import { HighlightRing } from '@/components/shared/SharedTasksSection';
import { Button } from '@/components/ui/button';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Bot,
  MoveHorizontal,
  Lightbulb,
  Target,
  Calendar as CalendarIcon,
  Trophy,
  Repeat,
  Clock,
  Compass, MoveRight, Check } from 'lucide-react';
import { format, isToday, isSameDay } from 'date-fns';
import { ContextSection } from './DayView';
import { useDesign } from '@/contexts/DesignContext';
import { StCard, priorityDot } from '@/components/studio/planner/parts';
import { formatTime12h } from '../../../utils/timeFormat';
import SuggestionsNotesPanel from '../SuggestionsNotesPanel';

// ─── Inline Editable Weekly Priorities Checklist ─────────────────────
function WeeklyPrioritiesChecklist({ priorities, setPriorities, setHoveredItem, onMove }) {
  const { isStudio } = useDesign();
  const inputRefs = useRef({});

  const updatePriority = (id, field, value) => {
    setPriorities(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const togglePriority = (id) => {
    setPriorities(prev => prev.map(p => p.id === id ? { ...p, completed: !p.completed } : p));
  };

  const addNewPriority = () => {
    const newId = `wp_${Date.now()}`;
    setPriorities(prev => [...prev, { id: newId, text: '', completed: false }]);
    setTimeout(() => inputRefs.current[newId]?.focus(), 50);
  };

  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNewPriority();
    }
    if (e.key === 'Backspace' && !priorities.find(p => p.id === id)?.text) {
      e.preventDefault();
      setPriorities(prev => prev.filter(p => p.id !== id));
    }
  };

  if (isStudio) {
    return (
      <div>
        {priorities.map((p) => (
          <div
            key={p.id}
            className={`st-prow${p.completed ? ' st-done' : ''}`}
            onMouseEnter={() => setHoveredItem?.({ type: 'weekly_priority', id: p.id, data: p, deleteFn: () => setPriorities(prev => prev.filter(item => item.id !== p.id)) })}
            onMouseLeave={() => setHoveredItem?.(null)}
          >
            <button
              type="button"
              className={`st-cb${p.completed ? ' st-on' : ''}`}
              aria-label={p.completed ? 'Mark not done' : 'Mark done'}
              onClick={() => togglePriority(p.id)}
              data-testid={`wp-check-${p.id}`}
            >
              {p.completed ? <Check /> : null}
            </button>
            <span className="st-txt">
              <input
                ref={el => inputRefs.current[p.id] = el}
                type="text"
                value={p.text}
                onChange={(e) => updatePriority(p.id, 'text', e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, p.id)}
                placeholder="Type a priority…"
                className="st-inline"
                data-testid={`wp-input-${p.id}`}
              />
            </span>
            {onMove && !p.completed && (p.text || '').trim() && (
              <span className="st-acts">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="st-ibtn" onClick={(e) => e.stopPropagation()} aria-label="Move priority" data-testid={`wp-move-${p.id}`}>
                      <MoveRight />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(p.id, 1); }} data-testid={`wp-move-next-${p.id}`}>Move to next week</DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(p.id, -1); }} data-testid={`wp-move-prev-${p.id}`}>Move to previous week</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            )}
          </div>
        ))}
        <button type="button" className="st-addrow" onClick={addNewPriority} data-testid="wp-add-btn">
          <span className="st-cb" />Add a priority
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {priorities.map((p) => (
        <div key={p.id} className="flex items-center gap-2 group"
          onMouseEnter={() => setHoveredItem?.({ type: 'weekly_priority', id: p.id, data: p, deleteFn: () => setPriorities(prev => prev.filter(item => item.id !== p.id)) })}
          onMouseLeave={() => setHoveredItem?.(null)}
        >
          <Checkbox
            checked={p.completed}
            onCheckedChange={() => togglePriority(p.id)}
            className="border-blue-300 dark:border-blue-800 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
            data-testid={`wp-check-${p.id}`}
          />
          <input
            ref={el => inputRefs.current[p.id] = el}
            type="text"
            value={p.text}
            onChange={(e) => updatePriority(p.id, 'text', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, p.id)}
            placeholder="Type a priority..."
            className={`flex-1 text-sm bg-transparent border-none outline-none placeholder:text-slate-300 ${
              p.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'
            }`}
            data-testid={`wp-input-${p.id}`}
          />
          {/* Move to next / previous week (2026-09-07) — same hover arrow tasks get */}
          {onMove && !p.completed && (p.text || '').trim() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/15 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  aria-label="Move priority"
                  data-testid={`wp-move-${p.id}`}
                >
                  <MoveRight className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(p.id, 1); }} data-testid={`wp-move-next-${p.id}`}>
                  Move to next week
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(p.id, -1); }} data-testid={`wp-move-prev-${p.id}`}>
                  Move to previous week
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
      <button
        onClick={addNewPriority}
        className="flex items-center gap-1.5 text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200 transition-colors mt-1"
        data-testid="wp-add-btn"
      >
        <Plus className="w-3 h-3" /> Add priority
      </button>
    </div>
  );
}

// ─── Day Column for the 7-day grid ──────────────────────────────────
function BiggestWinsList({ wins = [], setWins }) {
  const { isStudio } = useDesign();
  const inputRefs = useRef({});

  const updateWin = (id, text) => {
    if (wins.some(w => w.id === id)) {
      setWins(wins.map(w => w.id === id ? { ...w, text } : w));
    } else {
      setWins([...wins, { id, text }]);
    }
  };

  const addWin = () => {
    const id = `win-${Date.now()}`;
    setWins([...wins, { id, text: '' }]);
    setTimeout(() => inputRefs.current[id]?.focus(), 50);
  };

  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWin();
    }
    if (e.key === 'Backspace' && !wins.find(w => w.id === id)?.text) {
      e.preventDefault();
      if (wins.length > 1) {
        const idx = wins.findIndex(w => w.id === id);
        const prev = wins[idx - 1]?.id;
        setWins(wins.filter(w => w.id !== id));
        if (prev) setTimeout(() => inputRefs.current[prev]?.focus(), 50);
      }
    }
  };

  // Ensure at least one row
  const items = wins.length > 0 ? wins : [{ id: 'win-initial', text: '' }];

  if (isStudio) {
    return (
      <div>
        {items.map((w, idx) => (
          <div key={w.id} className="st-prow st-win">
            <span className="st-n">{idx + 1}</span>
            <span className="st-txt">
              <input
                ref={el => inputRefs.current[w.id] = el}
                type="text"
                value={w.text}
                onChange={(e) => updateWin(w.id, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, w.id)}
                placeholder={idx === 0 ? 'What went well last week?' : 'Another win…'}
                className="st-inline"
                data-testid={`win-input-${idx}`}
              />
            </span>
          </div>
        ))}
        <button type="button" className="st-addrow" onClick={addWin} data-testid="add-win-btn">
          <Plus />Add a win
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {items.map((w, idx) => (
        <div key={w.id} className="flex items-center gap-1.5">
          <span className="text-xs text-slate-400 dark:text-slate-500 font-medium w-4 text-right flex-shrink-0">{idx + 1}.</span>
          <input
            ref={el => inputRefs.current[w.id] = el}
            type="text"
            value={w.text}
            onChange={(e) => updateWin(w.id, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, w.id)}
            placeholder={idx === 0 ? "What went well last week?" : "Another win..."}
            className="flex-1 text-sm bg-transparent border-none outline-none placeholder:text-slate-300 dark:placeholder:text-slate-500 text-slate-700 dark:text-slate-200 py-0.5"
            data-testid={`win-input-${idx}`}
          />
        </div>
      ))}
      <button onClick={addWin} className="text-xs text-slate-400 dark:text-slate-500 hover:text-indigo-500 transition-colors ml-6 mt-1" data-testid="add-win-btn">
        + Add win
      </button>
    </div>
  );
}

function DayColumn({
  highlightSharedId, // R32
  day,
  isLast,
  isSelected,
  getTasksForDate,
  getEventsForDate,
  toggleTaskComplete,
  handleEditTask,
  handleDeleteClick,
  addTaskInline,
  moveTaskToDate,
  onSelect,
  dragOverDate,
  setDragOverDate,
  setHoveredItem,
}) {
  const { isStudio } = useDesign();
  const inputRefs = useRef({});
  const [pendingTasks, setPendingTasks] = useState([{ id: `pt_${Date.now()}`, text: '' }]);
  const dayStr = format(day, 'yyyy-MM-dd');
  const tasks = getTasksForDate(day);
  const events = [...getEventsForDate(day)].sort((a, b) => {
    if (a.all_day && !b.all_day) return -1;
    if (!a.all_day && b.all_day) return 1;
    return new Date(a.start) - new Date(b.start);
  });
  const isDragOver = dragOverDate === dayStr;

  const handleTextChange = (id, value) => {
    setPendingTasks(prev => prev.map(p => p.id === id ? { ...p, text: value } : p));
  };

  const handleKeyDown = (e, id) => {
    const item = pendingTasks.find(p => p.id === id);
    if (e.key === 'Enter') {
      e.preventDefault();
      if (item?.text.trim()) {
        addTaskInline(item.text.trim(), day);
        const newId = `pt_${Date.now()}`;
        setPendingTasks(prev => [...prev.filter(p => p.id !== id), { id: newId, text: '' }]);
        setTimeout(() => inputRefs.current[newId]?.focus(), 50);
      } else {
        const newId = `pt_${Date.now()}`;
        setPendingTasks(prev => {
          const idx = prev.findIndex(p => p.id === id);
          const copy = [...prev];
          copy.splice(idx + 1, 0, { id: newId, text: '' });
          return copy;
        });
        setTimeout(() => inputRefs.current[newId]?.focus(), 50);
      }
    }
    if (e.key === 'Backspace' && !item?.text) {
      e.preventDefault();
      if (pendingTasks.length > 1) {
        const idx = pendingTasks.findIndex(p => p.id === id);
        const prevId = pendingTasks[idx - 1]?.id;
        setPendingTasks(prev => prev.filter(p => p.id !== id));
        if (prevId) setTimeout(() => inputRefs.current[prevId]?.focus(), 50);
      }
    }
  };

  const dropHandlers = {
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverDate !== dayStr) setDragOverDate(dayStr);
    },
    onDragEnter: (e) => { e.preventDefault(); setDragOverDate(dayStr); },
    onDragLeave: (e) => { if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) setDragOverDate(null); },
    onDrop: (e) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');
      if (taskId) moveTaskToDate(taskId, dayStr);
      setDragOverDate(null);
    },
  };

  // ── Studio (106 §2, Week option A) ────────────────────────────────────────
  if (isStudio) {
    return (
      <div
        className={`st-wcol${isToday(day) ? ' st-today' : ''}${isDragOver ? ' st-drag' : ''}`}
        onClick={() => onSelect(day)}
        {...dropHandlers}
        data-testid={`day-col-${dayStr}`}
      >
        <div className="st-wch">
          <span className="st-dow">{format(day, 'EEE')}</span>
          <span className="st-dn">{format(day, 'd')}</span>
        </div>
        <div className="st-wbody">
          {events.map((event, idx) => (
            /* O4 (120): the native `title` tooltip painted far from its row. Classic
               carries no title here and, with D23's wrap restored, the full text is
               visible — so the attribute goes (classic parity, doc 105). */
            <div key={event.id || idx} className="st-wtask st-cal">
              <span className="st-pd" style={{ background: 'var(--st-fg3)', marginLeft: 5, marginRight: 5 }} />
              <span>{event.all_day ? event.title : `${event.title} · ${format(new Date(event.start), 'h:mm a')}`}</span>
            </div>
          ))}
          {tasks.map(task => (
            <div
              key={task.id}
              draggable
              onDragStart={(e) => { e.dataTransfer.setData('text/plain', task.id); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
              className={`st-wtask${task.completed ? ' st-done' : ''}`}
              onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
              onMouseEnter={() => setHoveredItem?.({ type: 'task', id: task.id, data: task })}
              onMouseLeave={() => setHoveredItem?.(null)}
              data-testid={`week-task-${task.id}`}
            >
              {highlightSharedId === task.id && <HighlightRing className="rounded-md" />}
              <button
                type="button"
                className={`st-cb${task.completed ? ' st-on' : ''}`}
                aria-label={task.completed ? 'Mark not done' : 'Mark done'}
                onClick={(e) => { e.stopPropagation(); toggleTaskComplete(task.id, task.completed); }}
                data-testid={`week-task-check-${task.id}`}
              >
                {task.completed ? <Check /> : null}
              </button>
              <span className="st-pd" style={{ background: priorityDot(task.priority) }} />
              <span>{task.title}{task.time ? ` · ${formatTime12h(task.time)}` : ''}</span>
              {/* D23 (120): `.st-rep` is sized by §33.5 now (13px, the §17.3 value), so
                  the hand-set top margin that compensated for the unsized 24px glyph goes. */}
              {(task.is_recurring || task.parent_task_id) && <Repeat className="st-rep" />}
            </div>
          ))}
          {pendingTasks.map((pt) => (
            <div key={pt.id} className="st-wtask st-add">
              <span className="st-cb" />
              <input
                ref={el => inputRefs.current[pt.id] = el}
                type="text"
                value={pt.text}
                onChange={(e) => handleTextChange(pt.id, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, pt.id)}
                onFocus={() => onSelect(day)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Type a task…"
                data-testid={`inline-add-${dayStr}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col transition-all min-h-[320px] ${!isLast ? 'border-r border-slate-200 dark:border-white/10' : ''} ${
        isDragOver
          ? 'bg-indigo-100/80 dark:bg-indigo-500/20 ring-2 ring-inset ring-indigo-400 dark:ring-indigo-400/70'
          : isSelected
            ? 'bg-indigo-50/40 dark:bg-indigo-500/[0.06]'
            : 'bg-white dark:bg-[#2d2f33]'
      }`}
      onClick={() => onSelect(day)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Continuously assert the highlight — engine-agnostic. WebKit's
        // dragleave coords/relatedTarget are unreliable (scroll-contaminated),
        // so this self-heals any wrong clear within one event tick.
        if (dragOverDate !== dayStr) setDragOverDate(dayStr);
      }}
      onDragEnter={(e) => { e.preventDefault(); setDragOverDate(dayStr); }}
      onDragLeave={(e) => {
        // Only trust relatedTarget when the engine provides it (Chromium /
        // Firefox). WebKit leaves it null AND reports scroll-contaminated
        // clientX/Y, so hit-testing misfires once the page is scrolled —
        // there we rely on the window-level dragend/drop cleanup instead.
        if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) setDragOverDate(null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData('text/plain');
        if (taskId) moveTaskToDate(taskId, dayStr);
        setDragOverDate(null);
      }}
      data-testid={`day-col-${dayStr}`}
    >
      {/* Day Header */}
      <div className={`px-2.5 py-2 border-b border-slate-100 dark:border-white/10 text-center ${
        isToday(day)
          ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
          : 'bg-indigo-50/80 dark:bg-indigo-500/10'
      }`}>
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${
          isToday(day) ? 'text-indigo-100' : 'text-indigo-400 dark:text-indigo-300/80'
        }`}>
          {format(day, 'EEE')}
        </div>
        <div className={`text-lg font-bold ${
          isToday(day) ? 'text-white' : 'text-indigo-900 dark:text-indigo-200'
        }`}>
          {format(day, 'd')}
        </div>
      </div>

      {/* Calendar Events */}
      {events.length > 0 && (
        <div className="px-2 py-1.5 bg-slate-50/80 dark:bg-white/[0.03] border-b border-slate-100 dark:border-white/10">
          {events.map((event, idx) => (
            <div key={event.id || idx} className="flex items-start gap-1.5 py-0.5">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
                style={{ backgroundColor: event.color || '#6366f1' }}
              />
              <div className="min-w-0 flex-1">
                <span className="text-[10px] text-slate-400 dark:text-slate-500 block leading-tight">
                  {event.all_day ? 'All day' : format(new Date(event.start), 'h:mm a')}
                </span>
                <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight block truncate">
                  {event.title}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task List + Inline Input (checklist pattern) */}
      <div className="flex-1 pl-0 pr-0.5 py-1.5 space-y-0.5 overflow-y-auto">
        {/* Existing tasks */}
        {tasks.map(task => (
          <div
            key={task.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', task.id);
              e.dataTransfer.effectAllowed = 'move';
              e.stopPropagation();
            }}
            className={`relative flex items-start gap-1 pl-0.5 pr-1 py-1 rounded-md group cursor-grab active:cursor-grabbing transition-colors ${
              task.completed
                ? 'opacity-60'
                : 'hover:bg-slate-50 dark:hover:bg-white/5'
            }`}
            onMouseEnter={() => setHoveredItem?.({ type: 'task', id: task.id, data: task })}
            onMouseLeave={() => setHoveredItem?.(null)}
            data-testid={`week-task-${task.id}`}
          >
            {highlightSharedId === task.id && <HighlightRing className="rounded-md" />}
            <div className="w-2.5 flex-shrink-0 flex items-center justify-center mt-1 opacity-0 group-hover:opacity-40 transition-opacity cursor-grab">
              <svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" className="text-slate-400 dark:text-slate-500">
                <circle cx="1" cy="1" r="1"/><circle cx="5" cy="1" r="1"/>
                <circle cx="1" cy="5" r="1"/><circle cx="5" cy="5" r="1"/>
                <circle cx="1" cy="9" r="1"/><circle cx="5" cy="9" r="1"/>
              </svg>
            </div>
            <PriorityCheckbox
              priority={task.priority}
              checked={task.completed}
              onCheckedChange={() => toggleTaskComplete(task.id, task.completed)}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
              data-testid={`week-task-check-${task.id}`}
            />
            <button
              onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
              className="flex-1 text-left min-w-0"
            >
              <span className={`text-xs leading-snug block ${
                task.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'
              }`}>
                {task.title}
              </span>
              {task.time && (
                <span className="text-[10px] text-indigo-500">{formatTime12h(task.time)}</span>
              )}
            </button>
            {(task.is_recurring || task.parent_task_id) && (
              <Repeat className="w-2.5 h-2.5 text-indigo-300 flex-shrink-0 mt-0.5" />
            )}
          </div>
        ))}

        {/* Inline checklist rows for adding tasks */}
        {pendingTasks.map((p) => (
          <div key={p.id} className="flex items-center gap-1 pl-0.5 pr-1 py-0.5">
            <div className="w-2.5 flex-shrink-0" />
            <Checkbox
              checked={false}
              disabled
              className="h-3.5 w-3.5 opacity-40 flex-shrink-0"
            />
            <input
              ref={el => inputRefs.current[p.id] = el}
              type="text"
              value={p.text}
              onChange={(e) => handleTextChange(p.id, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, p.id)}
              onFocus={() => onSelect(day)}
              placeholder="Type a task..."
              className="flex-1 text-xs bg-transparent border-none outline-none placeholder:text-slate-300 text-slate-600 dark:text-slate-300"
              data-testid={`inline-add-${dayStr}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main WeekView Component ────────────────────────────────────────
function WeekView({
  highlightSharedId, // R32
  onMovePriority, // weekly priority → next/previous week (2026-09-07)
  selectedDate,
  setSelectedDate,
  weekDays,
  getTasksForDate,
  getEventsForDate,
  showAddTask,
  setShowAddTask,
  newTask,
  setNewTask,
  addTask,
  toggleTaskComplete,
  handleEditTask,
  handleDeleteClick,
  getSuggestionsForDate,
  addSuggestion,
  handleMoveTask,
  suggestedTasksCollapsed,
  setSuggestedTasksCollapsed,
  panelMode,
  setPanelMode,
  dailyNotes,
  saveDailyNote,
  getPreviousWeekWins,
  setPreviousWeekWins,
  getWeeklyPriorities,
  setWeeklyPriorities,
  setHoveredItem,
  // New props
  addTaskInline,
  moveTaskToDate,
  monthlyPriorities,
  toggleMonthlyPriorityComplete,
  annualGoals,
  toggleAnnualGoalComplete,
  onNavigateToView,
  currentDate,
}) {
  const { isStudio } = useDesign();
  const [dragOverDate, setDragOverDate] = useState(null);

  // Always clear the drop highlight when the drag gesture ends anywhere —
  // the reliable end-of-drag signal on every engine (incl. WKWebView).
  useEffect(() => {
    const clear = () => setDragOverDate(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => {
      window.removeEventListener('dragend', clear);
      window.removeEventListener('drop', clear);
    };
  }, []);

  // Normalize data for ContextSection (same pattern as DayView)
  const monthlyItems = (monthlyPriorities || [])
    .map(p => ({ id: p.id, label: p.title, completed: p.completed }));

  const goalItems = [...(annualGoals || [])]
    .sort((a, b) => {
      const order = { 'Q1': 1, 'Q2': 2, 'Q3': 3, 'Q4': 4 };
      return (order[a.quarter] || 5) - (order[b.quarter] || 5);
    })
    .map(g => ({ id: g.id, label: g.title, completed: g.completed, badge: g.quarter || null }));

  const handleMonthlyToggle = (item) => {
    if (!toggleMonthlyPriorityComplete) return;
    const original = monthlyPriorities.find(p => p.id === item.id);
    if (original) toggleMonthlyPriorityComplete(original.id, original.completed);
  };

  const handleGoalToggle = (item) => {
    if (!toggleAnnualGoalComplete) return;
    const original = annualGoals.find(g => g.id === item.id);
    if (original) toggleAnnualGoalComplete(original.id, original.completed);
  };

  // ─── Desktop Layout ────────────────────────────────────────────
  const desktopView = (
    <div className="space-y-6">
      {/* 7-Day Column Grid */}
      <div className="grid grid-cols-7 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden" data-testid="week-grid">
        {weekDays.map((day, idx) => (
          <DayColumn
            highlightSharedId={highlightSharedId}
            key={idx}
            day={day}
            isLast={idx === 6}
            isSelected={isSameDay(day, selectedDate)}
            getTasksForDate={getTasksForDate}
            getEventsForDate={getEventsForDate}
            toggleTaskComplete={toggleTaskComplete}
            handleEditTask={handleEditTask}
            handleDeleteClick={handleDeleteClick}
            addTaskInline={addTaskInline}
            moveTaskToDate={moveTaskToDate}
            onSelect={setSelectedDate}
            dragOverDate={dragOverDate}
            setDragOverDate={setDragOverDate}
            setHoveredItem={setHoveredItem}
          />
        ))}
      </div>

      {/* Bottom Context Panel */}
      <div className="space-y-4">
        {/* Row 1: Suggested Tasks | Biggest Wins | Weekly Priorities */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Suggested Tasks / Daily Notes for Selected Day - Collapsible */}
          <SuggestionsNotesPanel
            selectedDate={selectedDate}
            collapsed={suggestedTasksCollapsed}
            setCollapsed={setSuggestedTasksCollapsed}
            mode={panelMode}
            setMode={setPanelMode}
            getSuggestionsForDate={getSuggestionsForDate}
            addSuggestion={addSuggestion}
            handleMoveTask={handleMoveTask}
            dailyNotes={dailyNotes}
            saveDailyNote={saveDailyNote}
            variant="week"
            testIdPrefix="week-"
          />

          {/* Biggest Wins */}
          <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card" data-testid="week-wins-card">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <Trophy className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                Biggest Wins
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                From last week
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <BiggestWinsList
                wins={getPreviousWeekWins()}
                setWins={setPreviousWeekWins}
              />
            </CardContent>
          </Card>

          {/* Weekly Priorities */}
          <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card" data-testid="week-priorities-card">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <Target className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                Weekly Priorities
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                Week of {format(weekDays[0], 'MMM d')}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <WeeklyPrioritiesChecklist
                onMove={onMovePriority}
                priorities={getWeeklyPriorities()}
                setPriorities={setWeeklyPriorities}
                setHoveredItem={setHoveredItem}
              />
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Monthly Priorities | Annual Goals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ContextSection
            icon={CalendarIcon}
            title={`${format(selectedDate || new Date(), 'MMMM')} Priorities`}
            items={monthlyItems}
            onToggle={handleMonthlyToggle}
            onNavigate={() => onNavigateToView?.('month')}
            emptyText="No priorities set for this month"
            accentColor="indigo"
          />
          <ContextSection
            icon={Compass}
            title={`${(currentDate || new Date()).getFullYear()} Goals`}
            items={goalItems}
            onToggle={handleGoalToggle}
            onNavigate={() => onNavigateToView?.('year')}
            emptyText="No annual goals set"
            accentColor="emerald"
          />
        </div>
      </div>
    </div>
  );

  // ─── Mobile Layout (stacked, select-a-day) ────────────────────
  const mobileView = (
    <div className="space-y-4">
      {/* Day Selector Strip */}
      <div className="grid grid-cols-7 gap-1.5">
        {weekDays.map((day, idx) => {
          const isSelected = isSameDay(day, selectedDate);

          return (
            <button
              key={idx}
              onClick={() => setSelectedDate(day)}
              className={`p-2 rounded-xl text-center transition-all ${
                isSelected
                  ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg shadow-indigo-500/30 scale-[1.02]'
                  : isToday(day)
                    ? 'bg-indigo-50 dark:bg-indigo-950/40 border-2 border-indigo-200 dark:border-indigo-900'
                    : 'bg-card border border-slate-200 dark:border-white/10'
              }`}
              data-testid={`mobile-day-${format(day, 'yyyy-MM-dd')}`}
            >
              <div className={`text-[10px] uppercase tracking-wider font-medium ${
                isSelected ? 'text-indigo-100' : isToday(day) ? 'text-indigo-500' : 'text-slate-400 dark:text-slate-500'
              }`}>
                {format(day, 'EEE')}
              </div>
              <div className={`text-lg font-bold ${
                isSelected ? 'text-white' : isToday(day) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200'
              }`}>
                {format(day, 'd')}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Day Events */}
      {getEventsForDate(selectedDate).length > 0 && (
        <div className="p-3 bg-slate-50 dark:bg-[#1e1f22] rounded-xl">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">Calendar</div>
          {[...getEventsForDate(selectedDate)]
            .sort((a, b) => {
              if (a.all_day && !b.all_day) return -1;
              if (!a.all_day && b.all_day) return 1;
              return new Date(a.start) - new Date(b.start);
            })
            .map((event, idx) => (
              <div key={event.id || idx} className="flex items-center gap-2 py-0.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: event.color || '#6366f1' }} />
                <span className="text-xs text-slate-500 dark:text-slate-400 w-14 flex-shrink-0">
                  {event.all_day ? 'All day' : format(new Date(event.start), 'h:mm a')}
                </span>
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{event.title}</span>
              </div>
            ))}
        </div>
      )}

      {/* Selected Day Tasks */}
      <Card className="shadow-sm border-0 bg-white/80 dark:bg-[#2d2f33]/80">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{format(selectedDate, 'EEEE')} Tasks</CardTitle>
            <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600" onClick={() => setShowAddTask(true)} data-testid="mobile-add-task-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {getTasksForDate(selectedDate).map(task => (
              <div
                key={task.id}
                className={`relative flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all ${
                  task.completed ? 'bg-card border-slate-200 dark:border-white/10' : 'bg-card border-slate-200 dark:border-white/10 hover:border-indigo-200'
                }`}
                onClick={() => handleEditTask(task)}
                data-testid={`mobile-task-${task.id}`}
              >
                {highlightSharedId === task.id && <HighlightRing className="rounded-xl" />}
                <PriorityCheckbox
                  priority={task.priority}
                  checked={task.completed}
                  onCheckedChange={() => toggleTaskComplete(task.id, task.completed)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm ${task.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
                    {task.title}
                  </span>
                  {task.time && (
                    <div className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatTime12h(task.time)}
                    </div>
                  )}
                </div>
                {(task.is_recurring || task.parent_task_id) && (
                  <Repeat className="w-3 h-3 text-indigo-400" />
                )}
              </div>
            ))}
            {getTasksForDate(selectedDate).length === 0 && (
              <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">No tasks for {format(selectedDate, 'EEEE')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile: Suggested Tasks / Daily Notes */}
      <SuggestionsNotesPanel
        selectedDate={selectedDate}
        collapsed={suggestedTasksCollapsed}
        setCollapsed={setSuggestedTasksCollapsed}
        mode={panelMode}
        setMode={setPanelMode}
        getSuggestionsForDate={getSuggestionsForDate}
        addSuggestion={addSuggestion}
        handleMoveTask={handleMoveTask}
        dailyNotes={dailyNotes}
        saveDailyNote={saveDailyNote}
        variant="day"
        testIdPrefix="mobile-"
      />

      {/* Mobile: Weekly Priorities */}
      <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Target className="w-4 h-4 text-blue-500 dark:text-blue-400" /> Weekly Priorities
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <WeeklyPrioritiesChecklist
                onMove={onMovePriority}
            priorities={getWeeklyPriorities()}
            setPriorities={setWeeklyPriorities}
            setHoveredItem={setHoveredItem}
          />
        </CardContent>
      </Card>

      {/* Mobile: Biggest Wins */}
      <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Trophy className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Biggest Wins
          </CardTitle>
          <CardDescription className="text-xs text-slate-500 dark:text-slate-400 ml-6">From last week</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <BiggestWinsList
            wins={getPreviousWeekWins()}
            setWins={setPreviousWeekWins}
          />
        </CardContent>
      </Card>

      {/* Mobile: Monthly + Annual Context */}
      <ContextSection
        icon={CalendarIcon}
        title={`${format(selectedDate || new Date(), 'MMMM')} Priorities`}
        items={monthlyItems}
        onToggle={handleMonthlyToggle}
        onNavigate={() => onNavigateToView?.('month')}
        emptyText="No priorities set for this month"
        accentColor="indigo"
      />
      <ContextSection
        icon={Compass}
        title={`${(currentDate || new Date()).getFullYear()} Goals`}
        items={goalItems}
        onToggle={handleGoalToggle}
        onNavigate={() => onNavigateToView?.('year')}
        emptyText="No annual goals set"
        accentColor="emerald"
      />
    </div>
  );

  // ── Studio (106 §2, Week option A) ───────────────────────────────────────────
  // The seven-column grid, then the three cards, then the two cards — each with
  // its own planner-hue tile (owner, 2026-09-10).  The mobile list below `lg` is
  // out of scope and keeps its classic look.
  if (isStudio) {
    return (
      <>
        <div className="hidden lg:block" data-testid="week-view-desktop">
          <div className="st-colmain">
            <div className="st-wk" data-testid="week-grid">
              {weekDays.map((day, idx) => (
                <DayColumn
                  highlightSharedId={highlightSharedId}
                  key={idx}
                  day={day}
                  isLast={idx === 6}
                  isSelected={isSameDay(day, selectedDate)}
                  getTasksForDate={getTasksForDate}
                  getEventsForDate={getEventsForDate}
                  toggleTaskComplete={toggleTaskComplete}
                  handleEditTask={handleEditTask}
                  handleDeleteClick={handleDeleteClick}
                  addTaskInline={addTaskInline}
                  moveTaskToDate={moveTaskToDate}
                  onSelect={setSelectedDate}
                  dragOverDate={dragOverDate}
                  setDragOverDate={setDragOverDate}
                  setHoveredItem={setHoveredItem}
                />
              ))}
            </div>

            <div className="st-three">
              <SuggestionsNotesPanel
                selectedDate={selectedDate}
                collapsed={suggestedTasksCollapsed}
                setCollapsed={setSuggestedTasksCollapsed}
                mode={panelMode}
                setMode={setPanelMode}
                getSuggestionsForDate={getSuggestionsForDate}
                addSuggestion={addSuggestion}
                handleMoveTask={handleMoveTask}
                dailyNotes={dailyNotes}
                saveDailyNote={saveDailyNote}
                variant="week"
                testIdPrefix="week-"
              />
              <StCard tile="planner" icon={<Trophy />} title="Biggest wins" small="From last week" testId="week-wins-card">
                <BiggestWinsList wins={getPreviousWeekWins()} setWins={setPreviousWeekWins} />
              </StCard>
              <StCard tile="planner" icon={<Target />} title="Weekly priorities" small={`Week of ${format(weekDays[0], 'MMM d')}`} testId="week-priorities-card">
                <WeeklyPrioritiesChecklist
                  onMove={onMovePriority}
                  priorities={getWeeklyPriorities()}
                  setPriorities={setWeeklyPriorities}
                  setHoveredItem={setHoveredItem}
                />
              </StCard>
            </div>

            <div className="st-two">
              <ContextSection
                icon={CalendarIcon}
                title={`${format(selectedDate || new Date(), 'MMMM')} priorities`}
                linkLabel="Open month"
                items={monthlyItems}
                onToggle={handleMonthlyToggle}
                onNavigate={() => onNavigateToView?.('month')}
                emptyText="No priorities set for this month"
                accentColor="indigo"
              />
              <ContextSection
                icon={Compass}
                title={`${(currentDate || new Date()).getFullYear()} goals`}
                linkLabel="Open year"
                items={goalItems}
                onToggle={handleGoalToggle}
                onNavigate={() => onNavigateToView?.('year')}
                emptyText="No annual goals set"
                accentColor="emerald"
              />
            </div>
          </div>
        </div>
        <div className="lg:hidden" data-testid="week-view-mobile">
          {mobileView}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block" data-testid="week-view-desktop">
        {desktopView}
      </div>
      {/* Mobile */}
      <div className="lg:hidden" data-testid="week-view-mobile">
        {mobileView}
      </div>
    </>
  );
}

export default WeekView;

